'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const careProvidedTypes = [
  'Residential',
  'Nursing',
  'Dementia',
  'Palliative',
];

export const distances = [5, 10, 20];

const defaultRatings = [
  { label: 'Outstanding', weighting: 'outstanding' },
  { label: 'Good', weighting: 'good' },
  { label: 'Requires improvement', weighting: 'requires_improvement' },
  { label: 'Inadequate', weighting: 'inadequate' },
  {
    label: 'Registered - awaiting routine inspection',
    weighting: 'awaiting_inspection',
  },
];

export const regulatorRatings = {
  cqc: defaultRatings,
  rqia: defaultRatings,
  ci_wales: defaultRatings,
  ci: [
    { label: 'Excellent', weighting: 'excellent' },
    { label: 'Very good', weighting: 'very_good' },
    { label: 'Good', weighting: 'good' },
    { label: 'Adequate', weighting: 'adequate' },
    { label: 'Weak', weighting: 'weak' },
    { label: 'Unsatisfactory', weighting: 'unsatisfactory' },
    { label: 'Not assessed', weighting: 'not_assessed' },
  ],
};

export const homeCareHourlyRateRanges = [
  { label: 'Up to £15', value: '0' },
  { label: '£15 - £20', value: '1' },
  { label: '£20 - £25', value: '2' },
  { label: '£25 or more', value: '3' },
];

export const homeCareVisitDurations = [
  { label: 'Any duration', value: 'on' },
  { label: '60 minutes or less', value: '60' },
  { label: '45 minutes or less', value: '45' },
  { label: '30 minutes or less', value: '30' },
  { label: '15 minutes', value: '15' },
];

export const sortOrders = {
  homeCare: [
    { label: 'Most relevant', value: 'relevance' },
    { label: 'Highest price', value: 'cah_starting_hourly_rate_desc' },
    { label: 'Lowest price', value: 'cah_starting_hourly_rate_asc' },
    { label: 'Highest rating', value: 'regulators_rating' },
  ],
  careHome: [
    { label: 'Most relevant', value: 'relevance' },
    { label: 'Nearest to me', value: 'distance' },
    { label: 'Highest rating', value: 'regulators_rating' },
  ],
};

export const careTypes = [
  { label: 'Care Home', value: 'careHome' },
  { label: 'Home Care', value: 'homeCare' },
];

export interface DirectoryInteractionsProps {
  isShowResultsInApp?: boolean;
  request?: any;
  response?: any;
  searchTerm?: string;
  children?: ReactNode;
}

export const DirectoryInteractionsContext = createContext<any>({});

export const DirectoryInteractionsProvider = ({
  isShowResultsInApp,
  children,
}: DirectoryInteractionsProps) => {
  // States
  const [results, setResults] = useState<any>(null);
  const [request, setRequest] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Contexts
  const initialCareProvidedState = careProvidedTypes.reduce(
    (all, current) => ({ ...all, [current]: false }),
    {},
  );
  const [careProvidedFilters, setCareProvidedFilters] = useState<any>(
    initialCareProvidedState,
  );
  const [currentDistance, setDistance] = useState(distances[0]);
  const [enabledRegulatorRatings, setEnabledRatings] = useState<any>({});
  const [enabledHourlyRates, setEnabledHourlyRates] = useState<any>({});
  const [enabledCareVisitDuration, setCareVisitDuration] = useState<any>(
    homeCareVisitDurations[0].value,
  );
  const [enabledSortOrder, setEnabledSortOrder] = useState<any>(
    sortOrders.careHome[0].value,
  );
  const [careType, setCareType] = useState('careHome');

  const toggleCareProvidedFilter = useCallback(
    (names: string[]) =>
      setCareProvidedFilters((val: any) => {
        let a: { [k: string]: boolean } = {};
        names.forEach((name) => (a[name] = true));
        return a;
      }),
    [setCareProvidedFilters],
  );

  const toggleRegulatorRating = useCallback(
    (weightings: string[]) => {
      let a: { [k: string]: boolean } = {};
      weightings.forEach((name) => (a[name] = true));

      setEnabledRatings(a);
    },
    [setEnabledRatings],
  );

  const toggleHourlyRate = useCallback(
    (names: string[]) => {
      let a: { [k: string]: boolean } = {};
      names.forEach((name) => (a[name] = true));

      setEnabledHourlyRates(a);
    },
    [setEnabledHourlyRates, enabledHourlyRates],
  );

  const setCurrentCareVisitDuration = setCareVisitDuration;

  const setCurrentDistance = useCallback(
    (val: any) => setDistance(Number(val)),
    [setDistance],
  );

  const enabledCareProvidedFilters = useMemo(
    () =>
      Object.keys(careProvidedFilters).filter(
        (filter) => careProvidedFilters[filter],
      ),
    [careProvidedFilters],
  );

  const setSortOrderToRelevance = useCallback(
    () => setEnabledSortOrder('relevance'),
    [setEnabledSortOrder],
  );

  const changeCareType = useCallback(
    (val: any) => {
      setSortOrderToRelevance();
      setCareType(val);
    },
    [setSortOrderToRelevance, setCareType],
  );

  const filters = useMemo(
    () => ({
      sortOrder: {
        careType,
        allValues: sortOrders,
        selectedValue: enabledSortOrder,
      },
      distance: {
        selectedValue: currentDistance,
        allValues: distances,
      },
      regulatorRatings: {
        allValues: regulatorRatings,
        selectedValues: enabledRegulatorRatings,
      },
      careProvided: {
        allValues: careProvidedTypes,
        selectedValues: enabledCareProvidedFilters,
      },
      hourlyRates: {
        allValues: homeCareHourlyRateRanges,
        selectedValues: enabledHourlyRates,
      },
      careVisitDuration: {
        allValues: homeCareVisitDurations,
        selectedValue: enabledCareVisitDuration,
      },
      careType: {
        allValues: careTypes,
        selectedValue: careType,
      },
    }),
    [
      careType,
      enabledSortOrder,
      currentDistance,
      enabledRegulatorRatings,
      enabledCareProvidedFilters,
      enabledHourlyRates,
      enabledCareVisitDuration,
    ],
  );

  const modifyFilter = useCallback(
    (filterKey: string, value: any) => {
      const fns: { [key: string]: (val: any) => void } = {
        sortOrder: setEnabledSortOrder,
        distance: setCurrentDistance,
        regulatorRatings: toggleRegulatorRating,
        careProvided: toggleCareProvidedFilter,
        hourlyRates: toggleHourlyRate,
        careVisitDuration: setCurrentCareVisitDuration,
        careType: changeCareType,
      };

      fns[filterKey](value);
    },
    [
      setEnabledSortOrder,
      setCurrentDistance,
      toggleRegulatorRating,
      toggleCareProvidedFilter,
      toggleHourlyRate,
      setCurrentCareVisitDuration,
      changeCareType,
    ],
  );

  // Callbacks
  const searchFn = useCallback(
    (term?: string) => {
      setRequest({
        searchTerm: term || searchTerm,
        filters,
        page: 1,
      });
    },
    [filters, searchTerm, setRequest],
  );

  const onGetMoreResults = useCallback(() => {
    setRequest({
      ...request,
      page: (request.page || 1) + 1,
    });
  }, [request]);

  const onProviderClick = (providerID: string) => {
    alert('clicked: ' + providerID);
  };

  const reset = useCallback(
    ({
      request: initialRequest,
      response: initialResponse,
      searchTerm: initialSearchTerm = '',
    }: DirectoryInteractionsProps) => {
      setRequest(initialRequest);
      setResults(initialResponse);
      setSearchTerm(initialSearchTerm);
    },
    [],
  );

  // Effects
  useEffect(() => {
    if (!request || filters === request.filters) return;
    searchFn();
  }, [filters, request, searchFn]);

  return (
    <DirectoryInteractionsContext.Provider
      value={{
        isShowResultsInApp,
        request,
        results,
        filters,
        searchTerm,
        setSearchTerm,
        setResults,
        modifyFilter,
        searchFn,
        onProviderClick,
        onGetMoreResults,
        reset,
      }}
    >
      {children}
    </DirectoryInteractionsContext.Provider>
  );
};
